import { useState } from 'react';
import ReactDOM from 'react-dom';
import PolygonIcon from '../../icons/PolygonIcon';
import EditPerimeterToolbar from './EditPerimeterToolbar';
import { emitCustomEvent } from 'react-custom-events';
import Events from '../../defaults/Events';
import './EditGeometryToolbar.css';
import Fab from '@mui/material/Fab';
import FieldNames from '../../defaults/FieldNames.json';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import EditPointToolbar from './EditPointToolbar';
import { useSelector } from 'react-redux';
import InFormAlert from '../InFormAlert';
import _ from 'lodash';
import { isFeaturePostFire } from '../../utils/FeatureHelper';
import EditLineToolbar from './EditLineToolbar';
import PolylineIcon from '@mui/icons-material/Polyline';

export default function EditGeometryToolbar({
    perimeter,
    onConfirmPerimeterEdit,
    onDrawComplete,
    handlePerimeterReset,
    onConfirmPointPerimeterEdit,
    onReplacePerimeter,
    onConfirmPointPostFirePerimeterEdit,
    onConfirmLinePostFirePerimeterEdit,
}) {
    /* Redux */
    const treatmentValidationUnsavedValues = useSelector((state) => {
        return state.treatmentValidation;
    });
    const activityValidationUnsavedValues = useSelector((state) => {
        return state.activityValidation;
    });
    const actualTreatmentValidationUnsavedValues = useSelector((state) => {
        return state.actualsTreatmentValidation;
    });
    const actualActivityValidationUnsavedValues = useSelector((state) => {
        return state.actualsActivityValidation;
    });
    const feature = useSelector((state) => state.details.feature);
    const isActual = useSelector((state) => state.details.isActual);
    const isTreatment = useSelector((state) => state.details.isTreatment);

    /* Local State */
    const [geometryField, setGeometryField] = useState(null);
    const [confirmDisabled, setConfirmDisabled] = useState(true);
    const [modifiedPerimeter, setModifiedPerimeter] = useState(null);
    const [tempFeatureType, setTempFeatureType] = useState(null);
    const [showGeometryConfirmPrompt, setShowGeometryConfirmPrompt] = useState(false);
    const [areUnsavedFields, setAreUnsavedFields] = useState(false);
    const [featureHasFunding, setFeatureHasFunding] = useState(false);

    /* Derived State */
    const isPostFire = isFeaturePostFire(feature?.attributes);
    const geometryPopupWarningText = // TODO: Move to copy file
        (areUnsavedFields
            ? `You have unsaved changes, if you want to keep those changes ` +
              `you must save before changing geometry. Do you want to continue?`
            : '') +
        (areUnsavedFields && featureHasFunding ? `` : '') +
        (featureHasFunding
            ? `NOTE:  When you make changes to the polygon for a treatment/activity,` +
              `the acres per funding line will be reset to default values (proportions of funding/acres). ` +
              `Please go to the Manage Costs screen review and ensure the proportions make sense for the ` +
              `treatment/activity, and manually update if desired.`
            : '');

    /* Workflow entry point - check geometry type */

    // UI Event
    const checkGeometryPrompt = (e) => {
        const featureType = e.target.dataset.geometryfield;
        const unsavedFields = areUnsavedFieldsOnFeature();
        const fundingExists = checkIfFundingExists(feature?.attributes);
        if (unsavedFields || fundingExists) {
            setAreUnsavedFields(unsavedFields);
            setFeatureHasFunding(fundingExists);
            setTempFeatureType(featureType);
            setShowGeometryConfirmPrompt(true);
        } else {
            setGeometryFieldState(featureType);
        }
    };

    const areUnsavedFieldsOnFeature = () => {
        let unsavedFields = {};
        if (isActual) {
            unsavedFields = isTreatment
                ? actualTreatmentValidationUnsavedValues
                : actualActivityValidationUnsavedValues;
        } else {
            unsavedFields = isTreatment
                ? treatmentValidationUnsavedValues
                : activityValidationUnsavedValues;
        }

        for (var prop in unsavedFields) {
            if (unsavedFields[prop] === true) {
                return true;
            }
        }

        return false;
    };

    const checkIfFundingExists = (currentFeature) => {
        const fundingExists =
            currentFeature &&
            currentFeature[FieldNames.featureFields.estimatedTotalCost] &&
            currentFeature[FieldNames.featureFields.estimatedTotalCost] > 0;

        return fundingExists ?? false;
    };

    /* Unsaved changes actions */

    // UI Event
    const onUnsavedChangesClose = () => {
        setShowGeometryConfirmPrompt(false);
    };

    // UI Event
    const onUnsavedChangesConfirm = () => {
        if (tempFeatureType) {
            setGeometryFieldState(tempFeatureType);
            setShowGeometryConfirmPrompt(false);
        }
    };

    /* Geometry Enabling methods */

    // Conditional handler based on what geometry type is being edited
    const setGeometryFieldState = (geometryField) => {
        /* Helper Function */
        const handleModifiedPerimeter = (args) => {
            setConfirmDisabled(false);
            setModifiedPerimeter(args);
            emitCustomEvent(Events.ModifiedGraphicChanged, args.geometry);
        };

        setGeometryField(geometryField);
        setConfirmDisabled(true);

        switch (geometryField) {
            case 'PERIMETER':
                if (perimeter === null || perimeter === undefined) {
                    emitCustomEvent(Events.EnableDrawMode, { geometry: null }).then((args) => {
                        handleModifiedPerimeter(args);
                    });
                } else {
                    enablePerimeterDraw();
                    setConfirmDisabled(false);
                }

                break;

            case 'POINT':
                if (perimeter === null || perimeter === undefined) {
                    if (isPostFire) {
                        emitCustomEvent(Events.ToggleIsMovingPoint, false);
                        if (feature) {
                            emitCustomEvent(Events.EnablePostFirePointMode, {
                                geometry: null,
                                callback: postFirePointFeatureChanged,
                                callbackParams: {
                                    isPostFire: true,
                                },
                            }).then((args) => {
                                handleModifiedPerimeter(args);
                            });
                        }
                    } else {
                        emitCustomEvent(Events.ToggleIsMovingPoint, false);
                        if (feature) {
                            emitCustomEvent(Events.EnablePointMode, {
                                geometry: null,
                                callback: pointFeatureChanged,
                                callbackParams: {
                                    isPostFire: false,
                                },
                            }).then((args) => {
                                handleModifiedPerimeter(args);
                            });
                        }
                    }
                } else {
                    if (isPostFire) {
                        emitCustomEvent(Events.ToggleIsMovingPoint, true);
                        enablePerimeterPointPostFire();
                        setConfirmDisabled(false);
                        emitCustomEvent(Events.ModifiedGraphicPostFirePointChanged, {
                            geometry: perimeter,
                        });
                    } else {
                        emitCustomEvent(Events.ToggleIsMovingPoint, true);
                        enablePerimeterPoint();
                        setConfirmDisabled(false);
                    }
                }

                break;

            case 'LINE':
                if (perimeter === null || perimeter === undefined) {
                    emitCustomEvent(Events.EnablePostFireLineMode, { geometry: null }).then(
                        (args) => {
                            handleModifiedPerimeter(args);
                        }
                    );
                } else {
                    enablePerimeterLine();
                    setConfirmDisabled(false);
                }

                break;

            default:
                break;
        }
    };

    // Helper function
    const setupTempPerimeter = () => {
        let tempPerimeter = null;
        if (perimeter) {
            tempPerimeter = perimeter.geometry ? perimeter.geometry : perimeter.perimeter;
        }
        return tempPerimeter;
    };

    // Callbacks within Geometry Enabling methods
    const pointModifiedChanged = (graphic) => {
        if (feature) {
            setExistingGeometry(graphic);
            const newFeature = Object.assign({}, feature);
            newFeature.geometry = graphic;
            newFeature.isTreatment = isTreatment;
            newFeature.isPostFire = true;
            newFeature.joinFeature = newFeature.joinFeature // Spidermen meme - Is this really necessary?
                ? newFeature.joinFeature
                : FieldNames.joinFeatureValues.point;
            setModifiedPerimeter(newFeature);
        }
    };

    const setExistingGeometry = (geo) => {
        if (feature) {
            let geom = geo;
            if ('geometry' in geom) {
                geom = geo.geometry;
            }
            emitCustomEvent(Events.SetExistingGeo, { geometry: geom, attr: feature.attributes });
        }
    };

    const pointFeatureChanged = (graphic) => {
        const newFeature = Object.assign({}, feature);
        if (newFeature) {
            newFeature.geometry = graphic.toJSON();
            newFeature.joinFeature = FieldNames.joinFeatureValues.polygon;
            newFeature.isTreatment = isTreatment;
            newFeature.isPostFire = false;
        }
        setModifiedPerimeter(newFeature);
        emitCustomEvent(Events.EstimatedPointCreated, newFeature);
        if (isTreatment) {
            onDrawComplete('treatment');
        } else {
            onDrawComplete('activity');
        }
    };

    const postFirePointFeatureChanged = (graphic) => {
        const newFeature = Object.assign({}, feature);
        newFeature.geometry = graphic.toJSON();
        newFeature.isTreatment = isTreatment;
        newFeature.isPostFire = true;
        newFeature.joinFeature = newFeature.joinFeature
            ? newFeature.joinFeature
            : FieldNames.joinFeatureValues.point;
        setModifiedPerimeter(newFeature);
        if (newFeature) {
            emitCustomEvent(Events.EstimatedPointCreated, newFeature);
            if (isTreatment) {
                onDrawComplete('treatment');
            } else {
                onDrawComplete('activity');
            }
        }
    };

    // Primary enabling methods
    const enablePerimeterDraw = () => {
        const geo = setupTempPerimeter();
        emitCustomEvent(Events.ToggleShouldUpdateGraphic, true);
        emitCustomEvent(Events.EnableDrawMode, {
            geometry: geo,
            attributes: feature?.attributes,
            callback: setExistingGeometry,
        });
    };

    const enablePerimeterLine = () => {
        const geo = setupTempPerimeter();
        emitCustomEvent(Events.ToggleShouldUpdateGraphic, true);
        emitCustomEvent(Events.EnablePostFireLineMode, {
            geometry: geo,
            callback: setExistingGeometry,
        });
    };

    const enablePerimeterPoint = () => {
        const geo = setupTempPerimeter();
        emitCustomEvent(Events.EnablePointMode, {
            geometry: geo,
            callback: pointModifiedChanged,
            callbackParams: {
                isPostFire: false,
            },
        });
    };

    const enablePerimeterPointPostFire = () => {
        const geo = setupTempPerimeter();
        emitCustomEvent(Events.EnablePostFirePointMode, {
            geometry: geo,
            callback: pointModifiedChanged,
            callbackParams: {
                isPostFire: true,
            },
        });
    };

    /* Upload perimeter method */

    // UI Event
    const onUploadPerimeter = (shapefilePerimeter) => {
        let tempFeature;
        if (feature && feature.geometry.type === shapefilePerimeter.type) {
            tempFeature = _.cloneDeep(feature);
            tempFeature.geometry = shapefilePerimeter;
            emitCustomEvent(Events.UploadGeometry, shapefilePerimeter);
        } else {
            throw Error(
                `Expecting ${feature.geometry.type} upload, received ${shapefilePerimeter.type}`
            );
        }
        emitCustomEvent(Events.ModifiedShapeUploaded, tempFeature);
        setModifiedPerimeter(tempFeature);
    };

    /* Cancel perimeter edit methods */

    // UI Event
    const cancelPerimeterEdit = () => {
        emitCustomEvent(Events.SetExistingGeo, { geometry: null, attr: null });
        emitCustomEvent(Events.DisableDrawMode);
        setGeometryFieldState(null);
        emitCustomEvent(Events.DisableExistingIcons, true);
        emitCustomEvent(Events.DisableNewActualIcons, true);
        emitCustomEvent(Events.DisableNewEstimatedIcons, false);
    };

    // UI Event
    const onClearChangesPerimeter = () => {
        handlePerimeterReset();
    };

    // UI Event
    const cancelPointOrLinePerimeterEdit = () => {
        emitCustomEvent(Events.SetExistingGeo, { geometry: null, attr: null });
        emitCustomEvent(Events.DisableDrawMode);
        setGeometryFieldState(null);
    };

    /* Confirm perimeter edit methods */

    // Helper function
    const handleConfirmPerimeterEdit = (shapeConfirmEvent, shouldClone = true) => {
        setGeometryFieldState(null);
        const args = shouldClone ? structuredClone(feature) : feature;
        if (modifiedPerimeter !== null) {
            args.geometry = modifiedPerimeter.geometry;
        }
        shapeConfirmEvent(args);
    };

    // TODO: The following four events are drilled all the way from the Header - not refactorable at this time

    // UI Event
    const confirmPerimeterEdit = () => handleConfirmPerimeterEdit(onConfirmPerimeterEdit);

    // UI Event
    const confirmPointPerimeterEdit = () => handleConfirmPerimeterEdit(onConfirmPointPerimeterEdit);

    // UI Event
    const confirmPointPostFirePerimeterEdit = () =>
        handleConfirmPerimeterEdit(onConfirmPointPostFirePerimeterEdit);

    // UI Event
    const confirmLinePostFirePerimeterEdit = () =>
        handleConfirmPerimeterEdit(onConfirmLinePostFirePerimeterEdit);

    /* Render method - conditional based on geometry type and workflow lifecycle */
    const renderEditGeometryToolbarOptions = (geometryField) => {
        if (!feature?.attributes) return;

        const featAttr = feature.attributes;
        const fields = FieldNames.featureFields;

        const isPoint =
            featAttr[fields.isPoint] === 'Yes' ||
            featAttr[fields.joinFeature] === FieldNames.joinFeatureValues.point;

        const isLine =
            !isPoint && featAttr[fields.joinFeature] === FieldNames.joinFeatureValues.line;

        const startEditingToolbarOptions = () => {
            switch (true) {
                case isPoint:
                    return (
                        <Fab
                            variant='extended'
                            size='medium'
                            color='primary'
                            aria-label='Edit'
                            data-geometryfield='POINT'
                            onClick={checkGeometryPrompt}
                        >
                            <GpsFixedIcon sx={{ mr: 1 }} />
                            Change Point
                        </Fab>
                    );

                case isLine:
                    return (
                        <Fab
                            variant='extended'
                            size='medium'
                            color='primary'
                            aria-label='Edit'
                            data-geometryfield='LINE'
                            onClick={checkGeometryPrompt}
                        >
                            <PolylineIcon sx={{ mr: 1 }} />
                            Edit Line
                        </Fab>
                    );

                default:
                    return (
                        <Fab
                            variant='extended'
                            size='medium'
                            color='primary'
                            aria-label='Edit'
                            data-geometryfield='PERIMETER'
                            onClick={checkGeometryPrompt}
                        >
                            <PolygonIcon sx={{ mr: 1 }} />
                            Edit Perimeter
                        </Fab>
                    );
            }
        };

        const activeEditingToolbarOptions = () => {
            switch (geometryField) {
                case 'PERIMETER':
                    return (
                        <EditPerimeterToolbar
                            isPostFire={isPostFire}
                            onConfirmPerimeterEdit={confirmPerimeterEdit}
                            onCancelPerimeterEdit={cancelPerimeterEdit}
                            onUploadPerimeter={onUploadPerimeter}
                            confirmDisabled={confirmDisabled}
                            onClearChangesPerimeter={onClearChangesPerimeter}
                            onReplacePerimeter={onReplacePerimeter}
                        />
                    );
                case 'POINT':
                    return (
                        <EditPointToolbar
                            onConfirmPointPerimeterEdit={confirmPointPerimeterEdit}
                            onCancelPointPerimeterEdit={cancelPointOrLinePerimeterEdit}
                            onConfirmPointPostFirePerimeterEdit={confirmPointPostFirePerimeterEdit}
                            isPostFire={isPostFire}
                        />
                    );
                case 'LINE':
                    return (
                        <EditLineToolbar
                            onCancelLinePerimeterEdit={cancelPointOrLinePerimeterEdit}
                            onConfirmLinePostFirePerimeterEdit={confirmLinePostFirePerimeterEdit}
                        />
                    );

                default:
                    return null;
            }
        };

        return (
            <div
                className='edit-geometry-container edit-geometry-selector'
                id='EditGeometryToolbarAnchorEl'
            >
                {geometryField === null
                    ? startEditingToolbarOptions()
                    : activeEditingToolbarOptions()}
            </div>
        );
    };

    // Doing it this way to properly position where the item shows up on the page
    return ReactDOM.createPortal(
        <>
            <div>
                <InFormAlert
                    openFlag={showGeometryConfirmPrompt}
                    closeMethod={onUnsavedChangesClose}
                    buttonMethod={onUnsavedChangesConfirm}
                    titleText={'Perimeter Warning'}
                    contentText={geometryPopupWarningText ?? 'Error'}
                />
            </div>
            <div className='edit-geometry-toolbar'>
                {renderEditGeometryToolbarOptions(geometryField)}
            </div>
        </>,
        document.getElementById('editPerimeterAnchorEl')
    );
}
